/**
 * Event notification and callback related types
 */
import type { StateEnum, User } from './user';
import type { MediaAction, MediaType } from './media';
import type {
  GroupInfo,
  GroupMemberInfoWithOperationType,
  GroupMemberRole,
  GroupStatusEnum,
} from './group';
import type { FileActionType } from './file';
import type { MessageEditStatus } from './message';
import type { StickerDownloadResult } from './emoji';

export enum WsStatus {
  Closed = 1,
  Connecting,
  Open,
  Error,
}

export enum PackageErrorActionType {
  REMOVE = 1,
  ADD = 2,
}

export enum PackageErrorType {
  PUSH_SINGLE_KEY = 1,
  PUSH_GROUP_KEY = 2,
  PULL_SINGLE_KEY = 3,
  PULL_GROUP_KEY = 4,
}

export interface PackageError {
  id: string;
  ope_type: PackageErrorActionType;
  error_type: PackageErrorType;
  related_id: string; // User id or group id
}

export enum GroupAdminChangedAction {
  Add = 'add',
  Remove = 'remove',
}

export enum GroupJoinsOperationType {
  Rejected = 1,
  Approved = 2,
  Pending = 3,
}

export type NoticeEventsType<S, M> = {
  // Local session data bulk snapshot
  // Triggered when update_sessions is called
  FullSessionsSnapshot: {
    sessions: S[];
  };

  // Complete session data update
  // Triggered when creating new session, including SDK auto-creation via new messages
  UpdateFullSession: {
    session: S;
  };

  // Session preview message update
  // 1. Triggered by new messages, including self-sent
  // 2. Triggered when messages are auto-destroyed or deleted
  SessionPreviewUpdated: {
    chat_id: string;
    last_message: M | undefined;
  };

  // Session unread count update
  // 1. Triggered when sending read receipts
  SessionUnreadCountUpdated: {
    chat_id: string;
    unread_count: number;
  };

  // Session basic info update
  // Triggered when receiving session avatar, name, or expire duration change notifications
  SessionBasicInfoUpdated: {
    chat_id: string;
    name: string;
    avatar: string;
    avatar_color: string;
    expire_duration: number;
  };

  // Session preview message status update
  // 1. Triggered when sending new message (if new message is preview message), status changes
  // 2. Triggered when receiving receipt notification (if message is preview message), status changes
  SessionPreviewStatusChanged: {
    chat_id: string;
    cli_msg_id: string;
    status: number;
  };

  SessionReceiptPreviewUpdated: {
    chat_id: string;
    cli_msg_id: string;
    receipt_status: number;
    read_state: {
      read_count: number;
      total_count: number;
    };
  };

  SessionAtListUpdated: {
    chat_id: string;
    at_message_list: string[];
  };

  SendMessage: {
    chat_id: string;
    message: M;
  };

  // New message notification received, triggered
  NewMessages: {
    chat_id: string;
    messages: M[];
  };

  // Send message, triggered when message send status changes
  MessageStatusChanged: {
    chat_id: string;
    status: number;
    cli_msg_id: string;
  };

  // Receipt received, triggered when receipt status changes
  MessageReceiptStatusChanged: {
    chat_id: string;
    receipt_status: number;
    message_id: string;
    cli_msg_id: string;
  };

  // Read received messages, sent messages fully read, triggered destruction timer notification
  MessageReadToDestroy: {
    chat_id: string;
    message_id: string;
    cli_msg_id: string;
    read_at: number;
  };

  // Referenced details update, triggered when referenced by other messages or when referenced message is deleted
  ReplyListChanged: {
    chat_id: string;
    message_id: string;
    cli_msg_id: string;
    reply_list: string[];
  };

  ReplyMessageDelete: {
    chat_id: string;
    message_id: string;
    cli_msg_id: string;
    child_cli_msg_id: string[];
  };

  AtMemberReadMessage: {
    chat_id: string;
    message_id: string;
    cli_msg_id: string;
    user_id: string;
    read_at: number;
  };

  // Message deletion notification, triggered by manual or expiry deletion, double deletion notification
  DeleteMessages: {
    chat_id: string;
    cli_msg_ids: string[];
  };

  /**
   * custom event, triggered when session is hidden or deleted
   */
  HideSessions: {
    chat_ids: string[];
    is_delete: boolean;
  };

  MessageEditStateChanged: {
    chat_id: string;
    message_id: string;
    status: MessageEditStatus;
    new_payload: any;
    edit_completed_at: number;
  };

  // Group chat message read count update
  ReadCountChanged: {
    chat_id: string;
    read_count: number;
    total_count: number;
    message_id: string;
    cli_msg_id: string;
  };

  // Contacts snapshot notification (currently unused)
  ContactsSnapshot: {
    contacts: User[];
  };

  // Group members snapshot notification, triggered when getting group member list
  GroupMembersChanged: {
    group_id: string;
    increment: GroupMemberInfoWithOperationType[];
  };

  // WebSocket network status update notification
  WsStateChanged: {
    state: number;
  };

  // Media call
  MediaCall: {
    chat_id: string;
    room_id: string;
    media_type: MediaType;
    sender_id: string;
  };

  // Media call status changed
  MediaCallStatusChanged: {
    chat_id: string;
    room_id: string;
    ope_type: MediaAction;
    sender_id: string;
  };

  MediaCallActionChanged: {
    chat_id: string;
    user_id: string;
    action: number;
  };

  // Base user info update
  ContactInfoListChanged: {
    data: User[];
  };

  UserStateChanged: {
    user_id: string;
    status: StateEnum;
  };

  UserProfileChanged: {
    user_id: string;
    update_profile: Map<string, string>;
  };

  GroupInfoChanged: {
    group_id: string;
    data: GroupInfo;
  };

  GroupOptionsChanged: {
    group_id: string;
    data: {
      key: string;
      val: number;
      category: number;
    }[];
  };

  MyGroupRoleChanged: {
    group_id: string;
    role: GroupMemberRole;
  };

  GroupOwnerChanged: {
    group_id: string;
    operator_user_id: string;
    new_owner_id: string;
  };

  GroupAdminChanged: {
    group_id: string;
    action: GroupAdminChangedAction;
    operator_user_id: string;
    user_ids: string[];
  };

  GroupJoinsChanged: {
    group_id: string;
    operation_type: GroupJoinsOperationType;
    user_id_list: string[];
  };

  GroupOptionsPartialChanged: {
    group_id: string;
    data: Map<string, string>;
  };

  GroupNameChanged: {
    group_id: string;
    name: string;
  };

  GroupDescriptionChanged: {
    group_id: string;
    description: string;
  };

  SessionExpiredTimeUpdated: {
    chat_id: string;
    expire_duration: number;
  };

  GroupStatusChanged: {
    group_id: string;
    status: GroupStatusEnum;
  };

  SessionCollapseChanged: {
    chat_id: string;
    collapse_group_id: string;
  };

  SessionOperateChanged: {
    chat_id: string;
    operate: number;
  };

  // File
  FileTransferProgress: {
    chat_id: string;
    cli_msg_id: string;
    file_id: string;
    context: FileActionType;
    progress: number;
  };

  FileTransferComplete: {
    chat_id: string;
    cli_msg_id: string;
    file_id: string;
    context: FileActionType;
    data: string;
  };

  FileTransferError: {
    chat_id: string;
    cli_msg_id: string;
    file_id: string;
    context: FileActionType;
    error: string;
  };

  NetworkError: {
    status_code: number;
  };

  PackageErrorChanged: {
    list: Array<PackageError>;
  };

  SessionLastReadSequenceUpdated: {
    chat_id: string;
    last_read_sequence: number;
  };

  StickerDownloadComplete: StickerDownloadResult;

  SessionPinnedUpdated: {
    chat_ids: string[];
  };
};

export type NotifyResponseType<E> = {
  event: keyof E;
  data: E[keyof E];
};
